<template>
  <div class="admin-entity-detail-page">
    <div class="admin-page-toolbar">
      <div class="toolbar-leading">
        <el-button @click="router.push('/admin/entities')">返回列表</el-button>
      </div>
      <div class="toolbar-actions">
        <div class="toolbar-operation">
          <div class="operation-name">视图</div>
          <el-radio-group
            v-model="graphState.viewValue"
            @change="handleViewChange"
          >
            <el-radio-button label="graph">
              <i class="iconfont icon-graph"></i>
            </el-radio-button>
            <el-radio-button label="catelogue">
              <i class="iconfont icon-list"></i>
            </el-radio-button>
          </el-radio-group>
        </div>
        <template v-if="isCatalogueEditable">
          <el-button @click="cancelEdit">取消</el-button>
          <el-button type="primary" :loading="state.saving" @click="saveEdit">
            保存
          </el-button>
        </template>
        <template v-else-if="!isEditMode">
          <el-button type="danger" @click="confirmDelete">删除</el-button>
        </template>
      </div>
    </div>

    <div class="admin-detail-content">
      <div
        v-show="graphState.viewValue === 'graph'"
        class="admin-graph-view-panel"
      >
        <div class="admin-graph-top-bar">
          <graph-floating-filter
            v-model="graphState.filterPanelExpanded"
            :filter-active="graphFilterActive"
            :overlay-target="adminGraphCanvasHostRef"
          >
            <graph-screen
              :key="entityId"
              ref="graphScreenRef"
              :entity-id="entityId"
              embedded
              @change="onScreenChange"
            />
          </graph-floating-filter>
        </div>
        <div ref="adminGraphCanvasHostRef" class="admin-graph-canvas-host">
          <graph-antv-graph
            :key="entityId"
            ref="graphAntvGraphRef"
            :center-entity-id="entityId"
            screen-value="hide"
            @init-data="onSearchGraph"
            @select-node="onSelectNode"
          />
        </div>
      </div>
      <el-scrollbar
        v-show="graphState.viewValue === 'catelogue'"
        class="admin-detail-scroll"
      >
        <entity-detail-presentation
          :entity-id="view.entityId"
          :name="view.name"
          :disambiguation="view.disambiguation"
          :type-list="view.typeList"
          v-model:type-codes="view.typeCodes"
          v-model:alias="view.alias"
          :category-options="state.entityCategoryOptions"
          v-model:description="view.description"
          v-model:attributes-list="view.attributesList"
          :relation-entities-list="view.relationEntitiesList"
          :ready="view.ready"
          :editable="isCatalogueEditable"
          :admin-mode="!isEditMode"
          @entity-click="onEntityClick"
          @relation-edit="openEditFromRelation"
          @relation-delete="confirmDeleteFromRelation"
        >
          <template v-if="!isEditMode" #basic-info-actions>
            <el-button type="primary" @click="goEdit">编辑基本信息</el-button>
          </template>
          <template v-if="!isEditMode" #relation-actions>
            <el-button
              type="primary"
              class="relation-create-btn"
              @click="openCreateDialog"
            >
              新建关系
            </el-button>
          </template>
        </entity-detail-presentation>
      </el-scrollbar>
    </div>

    <el-drawer
      v-model="state.peekDrawerVisible"
      direction="rtl"
      size="22%"
      :with-header="false"
      :modal="true"
      :close-on-click-modal="true"
      class="admin-entity-peek-drawer"
      @closed="onPeekDrawerClosed"
    >
      <entity-detail-peek-card
        v-if="state.peekEntityId"
        :key="state.peekEntityId"
        :entity-id="state.peekEntityId"
        show-close
        :show-delete-relation="peekShowDeleteRelation"
        title-clickable
        @close="closePeekDrawer"
        @title-click="onPeekTitleClick"
        @delete-relation="confirmDeletePeekRelation"
      />
    </el-drawer>

    <el-dialog
      v-model="state.dialogVisible"
      :title="state.dialogMode === 'create' ? '新建关系' : '编辑关系'"
      width="520px"
      destroy-on-close
      @closed="resetRelationForm"
    >
      <el-form label-width="100px">
        <el-form-item
          :label="state.dialogMode === 'create' ? '来源实体' : '目标实体'"
          required
        >
          <el-tooltip
            :content="selectedTargetEntityLabel"
            placement="top"
            :show-after="400"
            :disabled="!selectedTargetEntityLabel"
          >
            <div class="target-entity-select-wrap">
              <el-select
                v-model="relationForm.targetId"
                class="target-entity-select"
                popper-class="admin-target-entity-select-popper"
                fit-input-width
                filterable
                remote
                reserve-keyword
                :placeholder="
                  state.dialogMode === 'create'
                    ? '搜索来源实体（指向当前实体）'
                    : '搜索实体名称'
                "
                :remote-method="searchTargetEntities"
                :loading="state.targetSearchLoading"
              >
                <el-option
                  v-for="item in state.targetOptions"
                  :key="item.entityId"
                  :label="formatEntityOptionLabel(item)"
                  :value="item.entityId"
                >
                  <el-tooltip
                    :content="formatEntityOptionLabel(item)"
                    placement="top"
                    :show-after="300"
                  >
                    <span class="target-entity-option-text">{{
                      formatEntityOptionLabel(item)
                    }}</span>
                  </el-tooltip>
                </el-option>
              </el-select>
            </div>
          </el-tooltip>
        </el-form-item>
        <el-form-item label="关系类别" required>
          <el-select
            v-model="relationForm.typeCode"
            placeholder="选择关系类别"
            style="width: 100%"
          >
            <el-option
              v-for="c in state.relationCategoryOptions"
              :key="c.code"
              :label="`${c.name} (${c.code})`"
              :value="c.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="双向关系">
          <el-switch v-model="relationForm.isTwoWay" />
        </el-form-item>
        <el-form-item label="扩展属性">
          <div
            v-for="(attr, index) in relationForm.attributes"
            :key="index"
            class="attr-row"
          >
            <el-input
              v-model="attr.name"
              placeholder="属性名"
              style="width: 40%"
            />
            <el-input
              v-model="attr.value"
              placeholder="属性值"
              style="width: 40%"
            />
            <el-button link type="danger" @click="removeRelationAttr(index)">
              删除
            </el-button>
          </div>
          <el-button link type="primary" @click="addRelationAttr">
            + 添加属性
          </el-button>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="state.dialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="state.relationSubmitting"
          @click="submitRelation"
        >
          保存
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { ElMessage, ElMessageBox } from "element-plus";
import { httpPost, showApiErrorMessage } from "@/api/httpService";
import EntityDetailPresentation from "@/components/entity/EntityDetailPresentation.vue";
import EntityDetailPeekCard from "@/components/entity/EntityDetailPeekCard.vue";
import GraphScreen from "@/components/graph/GraphScreen.vue";
import GraphFloatingFilter from "@/components/graph/GraphFloatingFilter.vue";
import GraphAntvGraph from "@/components/graph/GraphAntvGraph.vue";
import {
  deleteAdminEntity,
  getAdminEntityDetail,
  listAdminEntityOptions,
  updateAdminEntity,
} from "@/api/adminEntityApi";
import { listRelationshipCategories } from "@/api/adminRelationshipCategoryApi";
import { searchEntityRelationAgg } from "@/api/graphApi";
import {
  createEntityRelation,
  deleteEntityRelation,
  listEntityRelations,
  updateEntityRelation,
} from "@/api/adminEntityRelationApi";

const route = useRoute();
const router = useRouter();

const entityId = computed(() => route.params.id);
const isEditMode = computed(() => route.path.endsWith("/edit"));
const defaultRelationEntityNum = 5;

const graphAntvGraphRef = ref(null);
const graphScreenRef = ref(null);
const adminGraphCanvasHostRef = ref(null);

const graphFilterActive = computed(
  () => graphState.typeList.length > 0 || graphState.relationList.length > 0
);

const graphState = reactive({
  filterPanelExpanded: false,
  viewValue: "catelogue",
  entityList: [],
  typeList: [],
  relationList: [],
  rawEntityDTOList: [],
  rawRelationDTOList: [],
  cachedGraphData: null,
});

let preloadGraphSeq = 0;

const isCatalogueEditable = computed(
  () => isEditMode.value && graphState.viewValue === "catelogue"
);

/** 仅「以下是当前实体的」入向关系可在侧栏删除（当前实体为 target） */
const peekShowDeleteRelation = computed(() => {
  const item = state.peekRelationItem;
  if (!item?.relationId) {
    return false;
  }
  return String(item.targetId) === String(entityId.value);
});

const view = reactive({
  ready: false,
  entityId: "",
  name: "",
  disambiguation: "",
  typeList: [],
  typeCodes: [],
  description: "",
  attributesList: [],
  relationEntitiesList: [],
  knowledgeGraphId: null,
  alias: [],
});

const state = reactive({
  peekDrawerVisible: false,
  peekEntityId: "",
  peekRelationItem: null,
  dialogVisible: false,
  dialogMode: "create",
  relationSubmitting: false,
  targetSearchLoading: false,
  targetOptions: [],
  relationCategoryOptions: [],
  entityCategoryOptions: [],
  saving: false,
});

const relationForm = reactive({
  relationId: "",
  targetId: "",
  typeCode: "",
  isTwoWay: false,
  attributes: [],
});

const selectedTargetEntityLabel = computed(() => {
  if (!relationForm.targetId) {
    return "";
  }
  const item = state.targetOptions.find(
    (o) => o.entityId === relationForm.targetId
  );
  return item ? formatEntityOptionLabel(item) : "";
});

function initRelationShowNums() {
  for (let r = 0; r < view.relationEntitiesList.length; r++) {
    view.relationEntitiesList[r].showNum = defaultRelationEntityNum;
    view.relationEntitiesList[r].incomingShowNum = defaultRelationEntityNum;
    view.relationEntitiesList[r].outgoingShowNum = defaultRelationEntityNum;
  }
}

function resetView() {
  view.ready = false;
  view.entityId = "";
  view.name = "";
  view.disambiguation = "";
  view.typeList = [];
  view.typeCodes = [];
  view.description = "";
  view.attributesList = [];
  view.relationEntitiesList = [];
  view.knowledgeGraphId = null;
  view.alias = [];
}

function emptyRelationForm() {
  relationForm.relationId = "";
  relationForm.targetId = "";
  relationForm.typeCode = "";
  relationForm.isTwoWay = false;
  relationForm.attributes = [];
}

function resetRelationForm() {
  emptyRelationForm();
}

function addRelationAttr() {
  relationForm.attributes.push({ name: "", value: "" });
}

function removeRelationAttr(index) {
  relationForm.attributes.splice(index, 1);
}

function resolveTypeCode(relationItem) {
  const byName = state.relationCategoryOptions.find(
    (c) => c.name === relationItem.type
  );
  if (byName) {
    return byName.code;
  }
  const byCode = state.relationCategoryOptions.find(
    (c) => c.code === relationItem.type
  );
  return byCode?.code || relationItem.type || "";
}

let loadDetailSeq = 0;

function applyDetailData(data, id) {
  view.entityId = data.entityId || id;
  view.name = data.name || "";
  view.disambiguation = data.disambiguation || "";
  view.typeList = data.typeList || [];
  view.typeCodes = data.typeCodes || [];
  view.description = data.description || "";
  view.attributesList = data.attributesList || [];
  view.knowledgeGraphId = data.knowledgeGraphId;
  view.alias = data.alias || [];
}

async function loadEntityCategoryOptions(graphId) {
  if (graphId == null) {
    return;
  }
  const res = await httpPost("/admin/entityCategory/v1/list", {
    knowledgeGraphId: graphId,
  });
  if (res?.code === 0) {
    state.entityCategoryOptions = res.data || [];
  }
}

async function loadRelationCategoryOptions(graphId) {
  if (graphId == null) {
    return;
  }
  const res = await listRelationshipCategories(graphId);
  if (res?.code === 0) {
    state.relationCategoryOptions = res.data || [];
  }
}

async function loadRelationAgg(id, seq) {
  const relRes = await searchEntityRelationAgg(id, null);
  if (seq !== loadDetailSeq) {
    return;
  }
  if (relRes?.code === 0) {
    view.relationEntitiesList = relRes.data || [];
    initRelationShowNums();
  }
}

async function loadDetail() {
  const seq = ++loadDetailSeq;
  const id = entityId.value;
  resetView();
  state.entityCategoryOptions = [];
  if (!id) {
    return;
  }
  try {
    const res = await getAdminEntityDetail(id);
    if (seq !== loadDetailSeq) {
      return;
    }
    if (res?.code !== 0) {
      showApiErrorMessage(res);
      return;
    }

    applyDetailData(res.data, id);
    view.ready = true;

    const graphId = view.knowledgeGraphId;
    if (graphId) {
      if (isEditMode.value) {
        loadEntityCategoryOptions(graphId);
      }
      loadRelationCategoryOptions(graphId);
    }
    await loadRelationAgg(id, seq);
    if (seq !== loadDetailSeq) {
      return;
    }
    scheduleGraphPreload();
  } catch {
    if (seq === loadDetailSeq) {
      ElMessage.error("加载实体详情失败");
    }
  }
}

function clearRelationSelection() {
  view.relationEntitiesList.forEach((group) => {
    (group.relationList || []).forEach((item) => {
      item.selected = false;
    });
  });
}

function onPeekDrawerClosed() {
  state.peekEntityId = "";
  state.peekRelationItem = null;
  clearRelationSelection();
}

function closePeekDrawer() {
  state.peekDrawerVisible = false;
}

function goEdit() {
  graphState.viewValue = "catelogue";
  router.push(`/admin/entities/${entityId.value}/edit`);
}

watch(
  () => graphState.filterPanelExpanded,
  () => {
    nextTick(() => graphAntvGraphRef.value?.initSize());
  }
);

function getGraphCenterId(data) {
  return data?.entityDTOList?.[0]?.entityId || "";
}

function syncGraphEntityListToRoute() {
  const id = entityId.value;
  if (!id) {
    graphState.entityList = [];
    return;
  }
  graphState.entityList = [id];
}

function handleViewChange(label) {
  if (label !== "graph") {
    graphState.filterPanelExpanded = false;
  }
  if (label === "graph") {
    if (isEditMode.value) {
      router.replace(`/admin/entities/${entityId.value}`);
    }
    syncGraphEntityListToRoute();
    graphState.typeList = [];
    graphState.relationList = [];
    nextTick(async () => {
      await ensureGraphRendered();
      graphAntvGraphRef.value?.initSize();
    });
  }
}

function onScreenChange(val) {
  graphState.typeList = val.typeList;
  graphState.relationList = val.relationList;
  searchGraph();
}

function getInitData(data) {
  return {
    nodes: (data.entityDTOList || []).map((v) => ({
      id: v.entityId,
      label: v.name,
      size: v.size,
      type: v.typeList?.[0],
    })),
    edges: (data.relationDTOList || []).map((v) => ({
      id: v.relationId,
      source: v.sourceId,
      target: v.targetId,
      label: v.type,
      relationship: v.type,
      type: v.curveOffset ? "arc" : "line",
      curveOffset: v.curveOffset,
      style: {
        startArrow: v.isTwoWay,
        endArrow: true,
      },
    })),
  };
}

function initGraphData(rawData, newData) {
  nextTick(() => {
    graphAntvGraphRef.value?.initGraph(
      getInitData(rawData),
      getInitData(newData)
    );
  });
}

async function fetchGraphPayload() {
  const id = entityId.value;
  if (!id) {
    return null;
  }
  if (!graphState.entityList.length || graphState.entityList[0] !== id) {
    graphState.entityList = [id];
  }
  const res = await httpPost("/graph/v1/searchGraph", {
    entityList: graphState.entityList,
    typeList: graphState.typeList,
    relationList: graphState.relationList,
  });
  if (res?.code !== 0 || !res.data) {
    return null;
  }
  res.data.entityDTOList?.forEach((v, i) => {
    v.size = i === 0 ? 40 : 20;
  });
  return res.data;
}

function applyGraphPayload(data) {
  const rawData = {
    entityDTOList: graphState.rawEntityDTOList,
    relationDTOList: graphState.rawRelationDTOList,
  };
  initGraphData(rawData, data);
  graphState.rawEntityDTOList = data.entityDTOList || [];
  graphState.rawRelationDTOList = data.relationDTOList || [];
  graphState.cachedGraphData = null;
}

async function searchGraph() {
  const data = await fetchGraphPayload();
  if (data) {
    applyGraphPayload(data);
  }
}

async function preloadGraphData() {
  const id = entityId.value;
  if (!id) {
    return;
  }
  const seq = ++preloadGraphSeq;
  const data = await fetchGraphPayload();
  if (seq !== preloadGraphSeq || !data) {
    return;
  }
  if (
    graphState.viewValue === "graph" &&
    graphState.rawEntityDTOList.length === 0
  ) {
    applyGraphPayload(data);
    nextTick(() => graphAntvGraphRef.value?.initSize());
    return;
  }
  graphState.cachedGraphData = data;
}

function scheduleGraphPreload() {
  graphState.cachedGraphData = null;
  graphState.rawEntityDTOList = [];
  graphState.rawRelationDTOList = [];
  preloadGraphData();
}

async function ensureGraphRendered() {
  const currentId = entityId.value;
  const rawCenterId = getGraphCenterId({
    entityDTOList: graphState.rawEntityDTOList,
  });
  if (graphState.rawEntityDTOList.length > 0 && rawCenterId === currentId) {
    return;
  }
  if (graphState.rawEntityDTOList.length > 0 && rawCenterId !== currentId) {
    graphState.rawEntityDTOList = [];
    graphState.rawRelationDTOList = [];
  }
  const cachedCenterId = getGraphCenterId(graphState.cachedGraphData);
  if (graphState.cachedGraphData && cachedCenterId === currentId) {
    applyGraphPayload(graphState.cachedGraphData);
    return;
  }
  if (graphState.cachedGraphData && cachedCenterId !== currentId) {
    graphState.cachedGraphData = null;
  }
  await searchGraph();
}

function onSearchGraph(model) {
  if (model?.id && !graphState.entityList.includes(model.id)) {
    graphState.entityList.push(model.id);
    searchGraph();
  }
}

function onSelectNode(model) {
  if (model?.id && String(model.id) !== String(entityId.value)) {
    router.push(`/admin/entities/${model.id}`);
  }
}

function resetGraphState() {
  preloadGraphSeq += 1;
  graphState.cachedGraphData = null;
  const id = entityId.value;
  graphState.entityList = id ? [id] : [];
  graphState.typeList = [];
  graphState.relationList = [];
  graphState.rawEntityDTOList = [];
  graphState.rawRelationDTOList = [];
}

function cancelEdit() {
  router.push(`/admin/entities/${entityId.value}`);
}

async function saveEdit() {
  if (!view.knowledgeGraphId) {
    ElMessage.warning("缺少图谱信息，无法保存");
    return;
  }
  state.saving = true;
  try {
    const res = await updateAdminEntity({
      entityId: view.entityId,
      knowledgeGraphId: view.knowledgeGraphId,
      name: view.name,
      alias: view.alias,
      disambiguation: view.disambiguation,
      description: view.description,
      typeCodes: view.typeCodes,
      attributes: (view.attributesList || [])
        .filter((a) => a.name?.trim())
        .map((a) => ({
          name: a.name.trim(),
          value: a.value == null ? "" : String(a.value),
        })),
    });
    if (res?.code === 0) {
      ElMessage.success("保存成功");
      router.push(`/admin/entities/${entityId.value}`);
    } else {
      showApiErrorMessage(res);
    }
  } finally {
    state.saving = false;
  }
}

async function confirmDeletePeekRelation() {
  const item = state.peekRelationItem;
  if (!item?.relationId || !item?.sourceId) {
    return;
  }
  try {
    await ElMessageBox.confirm(
      "确定删除与该实体的关系吗？实体本身不会被删除。",
      "删除关系",
      { type: "warning" }
    );
    const res = await deleteEntityRelation(item.relationId, item.sourceId);
    if (res?.code === 0) {
      ElMessage.success("已删除关系");
      closePeekDrawer();
      await loadDetail();
    } else {
      showApiErrorMessage(res);
    }
  } catch {
    // 取消
  }
}

function onPeekTitleClick(targetId) {
  if (!targetId) {
    return;
  }
  closePeekDrawer();
  if (String(targetId) !== String(entityId.value)) {
    router.push(`/admin/entities/${targetId}`);
  }
}

function onEntityClick({ relationItem, entityId: targetId }) {
  if (!targetId) {
    return;
  }
  clearRelationSelection();
  if (relationItem) {
    relationItem.selected = true;
  }
  state.peekEntityId = targetId;
  state.peekRelationItem = relationItem || null;
  state.peekDrawerVisible = true;
}

async function findOutboundRelation(relationId) {
  const res = await listEntityRelations(entityId.value);
  if (res?.code !== 0) {
    return null;
  }
  return (res.data?.outbound || []).find((r) => r.relationId === relationId);
}

async function openEditFromRelation(relationItem) {
  const row = await findOutboundRelation(relationItem.relationId);
  if (row) {
    openEditDialog(row);
    return;
  }
  state.dialogMode = "edit";
  relationForm.relationId = relationItem.relationId;
  relationForm.targetId = relationItem.targetId;
  relationForm.typeCode = resolveTypeCode(relationItem);
  relationForm.isTwoWay = Boolean(relationItem.isTwoWay);
  relationForm.attributes = [];
  state.targetOptions = [
    {
      entityId: relationItem.targetId,
      name: relationItem.targetName || relationItem.targetId,
      alias: [],
    },
  ];
  state.dialogVisible = true;
}

async function confirmDeleteFromRelation(relationItem) {
  const row = await findOutboundRelation(relationItem.relationId);
  if (row) {
    await confirmDeleteRelation(row);
  }
}

function formatEntityOptionLabel(item) {
  if (!item) {
    return "";
  }
  const name = item.name || item.entityId || "";
  const aliases = (item.alias || []).filter(
    (a) => a != null && String(a).trim()
  );
  if (!aliases.length) {
    return name;
  }
  return `${name}（${aliases.join("、")}）`;
}

async function searchTargetEntities(keywords) {
  if (!view.knowledgeGraphId) {
    state.targetOptions = [];
    return;
  }
  state.targetSearchLoading = true;
  try {
    const res = await listAdminEntityOptions({
      knowledgeGraphId: view.knowledgeGraphId,
      keywords: keywords?.trim() || undefined,
    });
    if (res?.code === 0) {
      const list = res.data || [];
      state.targetOptions = list.filter(
        (item) => item.entityId !== entityId.value
      );
    }
  } finally {
    state.targetSearchLoading = false;
  }
}

function openCreateDialog() {
  state.dialogMode = "create";
  emptyRelationForm();
  searchTargetEntities("");
  state.dialogVisible = true;
}

function openEditDialog(row) {
  state.dialogMode = "edit";
  relationForm.relationId = row.relationId;
  relationForm.targetId = row.targetId;
  relationForm.typeCode = row.typeCode;
  relationForm.isTwoWay = Boolean(row.isTwoWay);
  relationForm.attributes = (row.attributes || []).map((a) => ({
    name: a.name,
    value: a.value == null ? "" : String(a.value),
  }));
  state.targetOptions = [
    {
      entityId: row.targetId,
      name: row.targetName || row.targetId,
      alias: [],
    },
  ];
  state.dialogVisible = true;
}

async function submitRelation() {
  if (!relationForm.targetId || !relationForm.typeCode) {
    ElMessage.warning(
      state.dialogMode === "create"
        ? "请填写来源实体与关系类别"
        : "请填写目标实体与关系类别"
    );
    return;
  }
  const attrs = relationForm.attributes
    .filter((a) => a.name?.trim())
    .map((a) => ({ name: a.name.trim(), value: a.value }));
  state.relationSubmitting = true;
  try {
    let res;
    if (state.dialogMode === "create") {
      // 新建：所选实体为 source，当前实体为 target，归入「以下是 xxx 的」
      res = await createEntityRelation({
        entityId: relationForm.targetId,
        targetId: entityId.value,
        typeCode: relationForm.typeCode,
        isTwoWay: relationForm.isTwoWay,
        attributes: attrs,
      });
    } else {
      res = await updateEntityRelation({
        relationId: relationForm.relationId,
        entityId: entityId.value,
        targetId: relationForm.targetId,
        typeCode: relationForm.typeCode,
        isTwoWay: relationForm.isTwoWay,
        attributes: attrs,
      });
    }
    if (res?.code === 0) {
      ElMessage.success("保存成功");
      state.dialogVisible = false;
      await loadDetail();
    } else {
      showApiErrorMessage(res);
    }
  } finally {
    state.relationSubmitting = false;
  }
}

async function confirmDeleteRelation(row) {
  try {
    await ElMessageBox.confirm("确定删除该关系吗？", "删除关系", {
      type: "warning",
    });
    const res = await deleteEntityRelation(row.relationId, entityId.value);
    if (res?.code === 0) {
      ElMessage.success("已删除");
      await loadDetail();
    } else {
      showApiErrorMessage(res);
    }
  } catch {
    // 取消
  }
}

async function confirmDelete() {
  try {
    await ElMessageBox.confirm(
      "确定删除该实体吗？须先删除该实体与其他实体的全部关系。",
      "删除实体",
      { type: "warning" }
    );
    const res = await deleteAdminEntity(entityId.value);
    if (res?.code === 0) {
      ElMessage.success("已删除");
      router.push("/admin/entities");
    } else {
      showApiErrorMessage(res);
    }
  } catch {
    // 取消
  }
}

onMounted(() => {
  resetGraphState();
  if (isEditMode.value) {
    graphState.viewValue = "catelogue";
  }
  loadDetail();
});

watch(
  () => [route.params.id, route.path],
  ([id]) => {
    closePeekDrawer();
    if (isEditMode.value) {
      graphState.viewValue = "catelogue";
    }
    if (id) {
      resetGraphState();
      loadDetail();
    }
  }
);

watch(isCatalogueEditable, (editable) => {
  if (editable && view.knowledgeGraphId != null) {
    loadEntityCategoryOptions(view.knowledgeGraphId);
  }
});
</script>

<style lang="scss" scoped>
.admin-entity-detail-page {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: calc(100vh - 120px);

  .admin-page-toolbar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 0 16px;
    margin-bottom: 0;

    .toolbar-leading {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .toolbar-actions {
      display: flex;
      align-items: center;
      gap: 8px;

      .toolbar-operation {
        display: flex;
        align-items: center;
        margin-right: 16px;

        .operation-name {
          flex-shrink: 0;
          margin-right: 12px;
          font-size: 13px;
          color: #606266;
          line-height: 32px;
        }

        :deep(.el-radio-group) {
          line-height: 32px;
        }
      }
    }
  }

  .admin-detail-content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .admin-graph-view-panel {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  .admin-graph-top-bar {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 0 8px 8px 0;
    min-height: 34px;
  }

  .admin-graph-canvas-host {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  .admin-detail-scroll {
    flex: 1;
    min-height: 0;
  }

  .attr-row {
    display: flex;
    gap: 8px;
    margin-bottom: 8px;
    align-items: center;
  }

  .target-entity-select-wrap {
    width: 100%;
  }

  :deep(.relation-create-btn) {
    padding: 10px 20px;
    font-size: 15px;
    height: auto;
  }

  .target-entity-select {
    width: 100%;

    :deep(.el-select__wrapper) {
      width: 100%;
    }

    :deep(.el-select__selected-item),
    :deep(.el-select__placeholder) {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

:deep(.admin-entity-peek-drawer) {
  .el-drawer__body {
    padding: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
}
</style>

<style lang="scss">
.admin-target-entity-select-popper {
  .el-select-dropdown__item {
    padding-right: 12px;
  }

  .target-entity-option-text {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    line-height: 1.5;
  }
}
</style>
